import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ImageUploader from "../general/ImageUploader";
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, Switch, TextField } from "@mui/material";
import Gear from "../../models/gear";
import { AccountInfo } from "@azure/msal-browser";

interface AddEditGearProps {
    onWait: (wait: boolean) => void;
}

const AddEditGear: React.FC<AddEditGearProps> = ({onWait}) =>{
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { instance } = useMsal();
    const [account, setAccount] = useState<AccountInfo>();
    const [gear, setGear] = useState<Gear>({
            type: "",
            year: "",
            make: "",
            model: "",
            serialNumber: "",
            path: "",
            title: "",
            description: "",
            collection: "",
            photos: [],
            price: "",
            active: false
        });

    // useEffect to handle first load
    useEffect(() => {
        // start waiting
        onWait(true);
        window.scrollTo(0, 0);
        const firstLoad = async function () {
            await instance.initialize(); // Initialize MSAL
            await instance.handleRedirectPromise(); 
            const accounts = instance.getAllAccounts();
            if (accounts.length === 0) {
                instance.loginRedirect({
                    scopes: ["User.Read"]
                });
            }
            else {
                setAccount(accounts[0]);
                // authorize
                const authResp = await fetch("/api/Authorize", { method: "POST", headers: {
                    "Authorization": `Bearer ${accounts[0].idToken}`,
                    "Content-Type": "application/json",
                }});
                if (!authResp.ok) {
                    navigate("/");
                }
                
                if (params.path) {
                    // this is an edit...get the item from database
                    const response = await fetch(`/api/GetGearDetail?path=${params.path}&show_drafts=true`, {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                        },
                    });
                    const g = await response.json();
                    setGear(g);
                    onWait(false);
                }
                else {
                    // this is a create...initialize an empty gear object
                    onWait(false);
                }
            }
        };
        firstLoad();
    },[location.pathname]);

    const filesPicked = async (fileInfo:any) => {
        // add the new file to state using local URI (fileInfo.photo)
        setGear(prevGear => ({ ...prevGear, photos: [...prevGear.photos, fileInfo.photo] }));

        let formData = new FormData();
        formData.append(fileInfo.name, fileInfo.file);
        console.log("File buffer size: ", fileInfo.file.size);
        const response = await fetch(`/api/AddPhoto?path=${gear.path}&type=Gear`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${account?.idToken}`
            },
            body: formData
        });
        
        if (response.ok) {
            const newUri = await response.text();
            console.log(newUri);
            setGear(prevGear => ({
                ...prevGear,
                photos: prevGear.photos.map(photo => 
                    photo === fileInfo.photo ? newUri : photo
                )
            }));
        }
    }

    // handles input changes
    const handleInputChange = async (event:any) => {
        let fieldName = event.target.name;
        let fieldVal = event.target.value;
        setGear((prevGear: any) => {
            if (!prevGear) return { [fieldName]: fieldVal }; 
            return { 
                ...prevGear, 
                [fieldName]: fieldVal, 
                ...((fieldName === "year" || fieldName === "make" || fieldName === "model" || fieldName === "serialNumber") && { title: fixTitle(prevGear, fieldName, fieldVal) })
            };
        });
    };

    const fixTitle = (prevGear:Gear, fieldName:string, fieldVal:string) => {
        const year = (fieldName === "year") ? fieldVal : prevGear.year;
        const make = (fieldName === "make") ? fieldVal : prevGear.make;
        const model = (fieldName === "model") ? fieldVal : prevGear.model;
        const serial = (fieldName === "serialNumber") ? fieldVal : prevGear.serialNumber;
        return `${year} ${make} ${model} ${serial}`;
    };

    const save = async () => {
        onWait(true);
        if (gear.id) {
            // update
            const response = await fetch("/api/UpdateGear", {
                method: "PATCH",
                headers: {
                    "Authorization": `Bearer ${account?.idToken}`,
                    "Content-Type": "application/json",
                    "Accept":  "application/json"
                },
                body: JSON.stringify(gear)
            });
            //TODO: check if success
            navigate("/admin");
        }
        else {
            // create
            const response = await fetch("/api/CreateGear", {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${account?.idToken}`,
                    "Content-Type": "application/json",
                    "Accept":  "application/json"
                },
                body: JSON.stringify(gear)
            });
            var newGear:Gear = await response.json();
            console.log(newGear);
            navigate(`/admin/gear/edit/${newGear.path}`);
        }
        onWait(false);
    };

    const reorderPic = (index:number, type:number) => {
        // get instance of item in array
        let photoUrl = gear.photos[index];

        // remove existing item from array
        setGear(prevGear => ({
            ...prevGear,
            photos: prevGear.photos.filter((_, idx) => idx !== index)
        }));

        // determine new index for item
        let newIndex:number = 0;
        if (type == 1)
            newIndex = 0;
        else if (type == 2)
            newIndex = index - 1;
        else if (type == 3)
            newIndex = index + 1;
        else if (type == 4)
            newIndex = gear.photos.length;

        // add the item back in the correct location
        console.log(`Move ${index} to ${newIndex}`);
        setGear(prevGear => ({
            ...prevGear,
            photos: [
                ...prevGear.photos.slice(0, newIndex), 
                photoUrl, 
                ...prevGear.photos.slice(newIndex)
            ]
        }));
    };

    const deletePic = (index:number) => {
        // remove existing item from array
        setGear(prevGear => ({
            ...prevGear,
            photos: prevGear.photos.filter((_, idx) => idx !== index)
        }));
    };

    // return the JSX
    return (
        <div>
            <section className={"section-hero parallax--bg"}>
                <div className="container">
                    <h1 className="parallax--box">GEAR - {(gear.id) ? "EDIT" : "CREATE"}</h1>
                    <p>Use this form to configure the new gear item</p>
                </div>
            </section>
            <div style={{padding: "20px"}}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="flex-end" gap={2} style={{ paddingBottom: "10px" }}>
                            <Button onClick={() => { navigate("/admin") }} variant="outlined">Cancel</Button>
                            <Button onClick={save} variant="outlined">Save</Button>
                        </Box>
                    </Grid>
                    <Grid item sm={12} md={gear.id ? 6 : 12}>
                        <h3>Details</h3>
                        <FormControl fullWidth style={{paddingBottom: "10px", display: (gear.id) ? "block" : "none"}}>
                            <InputLabel shrink={true} id="activeLabel">Active</InputLabel>
                            <Switch checked={gear.active} value={gear?.active} onChange={(event:any) => { setGear(prevGear => ({...prevGear, active: !prevGear.active}))}}></Switch>
                        </FormControl>
                        <FormControl fullWidth style={{paddingBottom: "10px"}}>
                            <InputLabel id="collectionLabel">Collection</InputLabel>
                            <Select labelId="collectionLabel" id="collectionSelect" name="collection" label="Collection" value={gear?.collection} onChange={handleInputChange}>
                                <MenuItem value="inventory">Inventory</MenuItem>
                                <MenuItem value="portfolio">Portfolio</MenuItem>
                                <MenuItem value="personal">Personal</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth style={{paddingBottom: "10px"}}>
                            <InputLabel id="typeLabel">Type</InputLabel>
                            <Select labelId="typeLabel" id="typeSelect" name="type" label="Type" value={gear?.type} onChange={handleInputChange}>
                                <MenuItem value="Electric Guitar">Electric Guitar</MenuItem>
                                <MenuItem value="Acoustic Guitar">Acoustic Guitar</MenuItem>
                                <MenuItem value="Bass Guitar">Bass Guitar</MenuItem>
                                <MenuItem value="Amplifier">Amplifier</MenuItem>
                                <MenuItem value="Pedal/Effect">Pedal/Effect</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth style={{paddingBottom: "10px"}}>
                            <InputLabel id="yearLabel">Year</InputLabel>
                            <OutlinedInput id="yearTextField" name="year" label="Year" value={gear?.year} onChange={handleInputChange}></OutlinedInput>
                        </FormControl>
                        <FormControl fullWidth style={{paddingBottom: "10px"}}>
                            <InputLabel id="makeLabel">Make</InputLabel>
                            <OutlinedInput id="makeTextField" name="make" label="Make" value={gear?.make} onChange={handleInputChange}></OutlinedInput>
                        </FormControl>
                        <FormControl fullWidth style={{paddingBottom: "10px"}}>
                            <InputLabel id="modelLabel">Model</InputLabel>
                            <OutlinedInput id="modelTextField" name="model" label="Model" value={gear?.model} onChange={handleInputChange}></OutlinedInput>
                        </FormControl>
                        <FormControl fullWidth style={{paddingBottom: "10px"}}>
                            <InputLabel htmlFor="serialNumberInput">Serial Number</InputLabel>
                            <OutlinedInput id="serialNumberInput" name="serialNumber" label="Serial Number" value={gear?.serialNumber} onChange={handleInputChange} />
                        </FormControl>
                        <FormControl fullWidth style={{paddingBottom: "10px"}}>
                            <InputLabel htmlFor="titleInput">Title</InputLabel>
                            <OutlinedInput id="titleInput" name="title" label="Title" value={gear?.title} onChange={handleInputChange} />
                        </FormControl>
                        <FormControl fullWidth style={{paddingBottom: "10px"}}>
                            <InputLabel htmlFor="descriptionInput">Description</InputLabel>
                            <OutlinedInput multiline id="descriptionInput" name="description" label="Description" rows={6} value={gear?.description} onChange={handleInputChange}/>
                        </FormControl>
                        <FormControl fullWidth style={{paddingBottom: "10px", visibility: (gear.collection === "inventory") ? "visible" : "hidden", height: (gear.collection === "inventory") ? "auto" : 0}}>
                            <InputLabel htmlFor="priceInput">Price</InputLabel>
                            <OutlinedInput id="priceInput" name="price" label="Price" value={gear?.price} onChange={handleInputChange}/>
                        </FormControl>
                    </Grid>
                    <Grid item sm={12} md={gear.id ? 6 : 12} style={gear.id ? {display: "block"} : {display: "none"}}>
                        <ImageUploader 
                            style={{display: (gear.id) ? "block" : "none"}}
                            images={gear.photos}
                            onDelete={deletePic} 
                            onReorder={reorderPic} 
                            filesPicked={filesPicked}></ImageUploader>
                    </Grid>
                </Grid>
            </div>
        </div>
    );
}

export default AddEditGear;
